(() => {
  const ready = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  ready(() => {
    const modal = document.querySelector('[data-p2c-size-guide-modal]');
    if (!modal) return;

    const dialog = modal.querySelector('.p2c-size-guide-modal__dialog');
    const closeButton = modal.querySelector('.p2c-size-guide-modal__close');
    let previousFocus = null;

    const openModal = () => {
      previousFocus = document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('p2c-size-guide-open');

      window.requestAnimationFrame(() => {
        closeButton?.focus();
      });
    };

    const closeModal = () => {
      modal.hidden = true;
      document.documentElement.classList.remove('p2c-size-guide-open');

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('a[href="#p2c-size-guide"]');
      if (!trigger) return;

      event.preventDefault();
      openModal();
    });

    modal.querySelectorAll('[data-p2c-size-guide-close]').forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key === 'Tab' && dialog) {
        const focusable = [
          ...dialog.querySelectorAll(
            'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ].filter((element) => element.offsetParent !== null);

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  });
})();

/* Match the desktop purchase panel to the rendered gallery height.
   Benefits stay in their native position and are pushed to the panel bottom. */
(() => {
  const desktopQuery = window.matchMedia('(min-width: 750px)');

  const initialisePurchasePanelHeight = (scope = document) => {
    scope.querySelectorAll?.('.product-information').forEach((productInformation) => {
      if (productInformation.dataset.p2cPanelHeightReady === 'true') return;

      const gallery = productInformation.querySelector(
        '.product-information__media media-gallery'
      );
      const details = productInformation.querySelector('.product-details');

      if (!gallery || !details) return;

      const syncHeight = () => {
        if (!desktopQuery.matches) {
          details.style.removeProperty('--p2c-gallery-height');
          return;
        }

        const galleryHeight = Math.ceil(gallery.getBoundingClientRect().height);
        if (galleryHeight > 0) {
          details.style.setProperty('--p2c-gallery-height', `${galleryHeight}px`);
        }
      };

      productInformation.dataset.p2cPanelHeightReady = 'true';
      syncHeight();

      const resizeObserver = new ResizeObserver(syncHeight);
      resizeObserver.observe(gallery);
      desktopQuery.addEventListener('change', syncHeight);
    });
  };

  const start = () => {
    initialisePurchasePanelHeight();

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length)) {
        initialisePurchasePanelHeight();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* V18.17 — Shopify may inject/repaint the accelerated-checkout link after
   the theme CSS has loaded. Apply an inline high-priority accessibility fix
   whenever the link appears or is replaced. */
(() => {
  const LINK_SELECTOR =
    'more-payment-options-link a.shopify-payment-button__more-options, ' +
    'a#more-payment-options-link.shopify-payment-button__more-options';

  const stylePaymentOptionsLink = (root = document) => {
    root.querySelectorAll?.(LINK_SELECTOR).forEach((link) => {
      const styles = {
        display: 'flex',
        width: 'max-content',
        'max-width': '100%',
        'min-height': '40px',
        margin: '0 auto',
        padding: '8px 12px',
        'align-items': 'center',
        'justify-content': 'center',
        color: '#3b2d27',
        '-webkit-text-fill-color': '#3b2d27',
        opacity: '1',
        visibility: 'visible',
        'font-size': '14px',
        'font-weight': '700',
        'line-height': '1.25',
        'text-align': 'center',
        'text-decoration-line': 'underline',
        'text-decoration-color': '#b07a43',
        'text-decoration-thickness': '1.5px',
        'text-underline-offset': '4px',
        'text-shadow': 'none',
        background: 'transparent',
        filter: 'none',
        'mix-blend-mode': 'normal'
      };

      Object.entries(styles).forEach(([property, value]) => {
        link.style.setProperty(property, value, 'important');
      });
    });
  };

  const start = () => {
    stylePaymentOptionsLink();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          stylePaymentOptionsLink();
          break;
        }
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element &&
          (mutation.target.matches?.(LINK_SELECTOR) ||
            mutation.target.closest?.('more-payment-options-link'))
        ) {
          stylePaymentOptionsLink();
          break;
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-disabled']
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
